import { ref, watch, shallowRef, markRaw } from 'vue'
import { defineStore } from 'pinia'
import { useApi } from '@/composables/useApi'
import { useMapPrefs } from '@/composables/useMapPrefs'
import { useThemeStore } from '@/store/useThemeStore'

import * as Lvue from "leaflet";
import * as fIcons from './mix/iconsNumbers'
import * as LIcons from './mix/iconsLetters'

import { GestureHandling } from "leaflet-gesture-handling";
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";
import { DEFAULT_CENTER, TILE_LAYERS, GOOGLE_SATELLITE, DARK_STREET_LAYER } from './mapConstants'

if (!Lvue.Map.__gestureHandlingRegistered) {
  Lvue.Map.addInitHook("addHandler", "gestureHandling", GestureHandling);
  Lvue.Map.__gestureHandlingRegistered = true;
}

export const useMapStorage = defineStore('mapa', () => {
  const center = ref(DEFAULT_CENTER)

  const setCenter = (val) => {
    if (val?.lat) {
      center.value = [val.lat, val.lng]
      return
    }
    if (Array.isArray(val)) {
      center.value = val
      return
    }
    if (val?.center) {
      if (Array.isArray(val.center)) {
        center.value = val.center
        return
      }
      if (val.center.lat) {
        center.value = [val.center.lat, val.center.lng]
        return
      }
      if (val.center?.center) {
        center.value = val.center.center
      }
    }
  }

  return { center, setCenter }
})


export const useMapStore = () => {
  const { center, setCenter } = useMapStorage()
  const { googleEnabled, satelliteEnabled, setSatelliteEnabled } = useMapPrefs()
  const themeStore = useThemeStore()
  const map = shallowRef(null)
  let currentTileLayer = null

  let markers = [];
  let layerGroups = [];

  const sectorOption = {
    color: 'red',
    weight: 3,
    opacity: 0.5,
    smoothFactor: 1
  }

  const iconMarker = [
    Lvue.icon({
      iconUrl: '/pe.png',
      iconSize: [32, 32],
      alt: 'Punto de Encuentro'
    })
  ]

  const icons = fIcons.getIcons(Lvue);
  const nIcons = shallowRef(markRaw(icons));
  const iconsLetter = LIcons.getIcons(Lvue)
  const lIcons = shallowRef(markRaw(iconsLetter));
  const tile = ref('OpenStreetMap');
  const layers = ref(Object.keys(TILE_LAYERS));

  function setMap(data) {
    map.value = data ? markRaw(data) : null
    if (data) setEvents()
  }

  function getCenter() {
    const mapCenter = map.value.getBounds().getCenter()
    return [mapCenter.lat, mapCenter.lng];
  }

  const getStoreCenter = () => {
    if (center?.lat) return { lat: center.lat, lng: center.lng }
    const arr = Array.isArray(center) ? center : center.center
    return { lat: arr[0], lng: arr[1] }
  }

  function setTileLayer() {
    let config
    if (googleEnabled.value) {
      config = satelliteEnabled.value ? GOOGLE_SATELLITE : TILE_LAYERS['Google']
    } else if (satelliteEnabled.value) {
      config = TILE_LAYERS['ESRI Satellite']
    } else {
      // El callejero por defecto (sin selector de capa manual activo) cambia
      // a su variante oscura cuando la app está en modo oscuro.
      const isDefaultStreet = tile.value === 'OpenStreetMap'
      config = isDefaultStreet && themeStore.theme === 'dark'
        ? DARK_STREET_LAYER
        : (TILE_LAYERS[tile.value] ?? TILE_LAYERS['OpenStreetMap'])
    }
    if (currentTileLayer) {
      currentTileLayer.remove()
      currentTileLayer = null
    }
    currentTileLayer = Lvue.tileLayer(config.url, {
      attribution: config.attribution,
      ...(config.subdomains ? { subdomains: config.subdomains } : {}),
      ...(config.maxZoom ? { maxZoom: config.maxZoom } : {}),
    }).addTo(map.value)
  }

  const toggleSatellite = () => {
    setSatelliteEnabled(!satelliteEnabled.value)
    if (map.value) setTileLayer()
  }

  // Si el usuario alterna modo oscuro con el mapa ya abierto, re-aplica la
  // capa de calles para reflejar el cambio (el satélite no se ve afectado).
  watch(() => themeStore.theme, () => {
    if (map.value && !satelliteEnabled.value) setTileLayer()
  })

  const addMarker = (marker) => {
    markers.push(marker);
    marker.addTo(map.value);
  }

  const setEvents = () => {
    map.value.on('zoomstart', async function() {
      await removeLayers();
    })
    map.value.on('zoomend', function() {
      addLayers();
    })
  }

  const removeLayers = () => {
    for (let i = 0; i < layerGroups.length; i++) {
      layerGroups[i].remove();
    }
  }

  const addLayers = () => {
    layerGroups.forEach(layer => {
      layer.addTo(map.value);
    });
  }

  const addLayerGroup = (newGroup, toRemoveGroup) => {
    if (toRemoveGroup) {
      toRemoveGroup.remove();
      layerGroups = layerGroups.filter(layer => layer !== toRemoveGroup);
    }
    const layer = newGroup.addTo(map.value);
    layerGroups.push(layer);
    return layer;
  }

  const on = (event, callback) => {
    map.value.on(event, callback);
  }

  const geocodeFinder = ref(null)

  const placeFinder = (place) => {
    const coords = place ? [place.lat, place.lon] : DEFAULT_CENTER
    geocodeFinder.value = place
    setCenter(coords)
    map.value.flyTo(coords, 16);
  }

  async function geocode(address) {
    try {
      const api = useApi()
      const data = await api.get(`/api/geocode/search?q=${encodeURIComponent(address)}`)
      if (!data || data.length === 0) {
        placeFinder(null)
        return null
      }
      placeFinder(data[0])
      return data[0]
    } catch (e) {
      placeFinder(null)
      return null
    }
  }

  return {
    map,
    on,
    nIcons,
    lIcons,
    sectorOption,
    iconMarker,
    L: Lvue, // alias para compatibilidad con consumidores existentes
    setMap,
    geocode,
    getCenter,
    setCenter,
    addMarker,
    addLayerGroup,
    center,
    getStoreCenter,
    layers,
    tile,
    satellite: satelliteEnabled,
    setTileLayer,
    toggleSatellite,
  }
}
